import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  ScrollView,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { driverService } from '../../services/driverService';

const isWeb = Platform.OS === 'web';

const DOCUMENT_TYPES = [
  { type: 'DRIVERS_LICENSE', label: "Driver's License", icon: 'card', required: true },
  { type: 'VEHICLE_INSURANCE', label: 'Vehicle Insurance', icon: 'shield-checkmark', required: true },
  { type: 'VEHICLE_INSPECTION', label: 'Vehicle Inspection', icon: 'car', required: true },
  { type: 'LIMO_LICENSE', label: 'Limo / Commercial License', icon: 'ribbon', required: false },
];

const STATUS_COLORS = {
  PENDING: '#FF9800',
  APPROVED: '#4CAF50',
  REJECTED: '#F44336',
};

const STATUS_LABELS = {
  PENDING: 'Pending Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

const DriverAccountScreen = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  // Vehicle info
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleYear, setVehicleYear] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [phone, setPhone] = useState('');

  // Documents
  const [documents, setDocuments] = useState([]);
  const [driverStatus, setDriverStatus] = useState('PENDING_APPROVAL');

  const loadProfile = useCallback(async () => {
    try {
      const response = await driverService.getProfile();
      if (response.success) {
        const d = response.data;
        setVehicleMake(d.vehicleMake || '');
        setVehicleModel(d.vehicleModel || '');
        setVehicleYear(d.vehicleYear ? String(d.vehicleYear) : '');
        setVehicleColor(d.vehicleColor || '');
        setVehiclePlate(d.vehiclePlate || '');
        setPhone(d.phone || '');
        setDocuments(d.driverDocuments || []);
        setDriverStatus(d.driverStatus || 'PENDING_APPROVAL');
      }
    } catch (error) {
      console.error('Load profile error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveVehicle = async () => {
    setSaving(true);
    try {
      const response = await driverService.updateProfile({
        vehicleMake, vehicleModel,
        vehicleYear: vehicleYear ? parseInt(vehicleYear) : null,
        vehicleColor, vehiclePlate, phone,
      });
      if (response.success) {
        if (isWeb) window.alert('Vehicle info saved!');
      } else {
        if (isWeb) window.alert(response.message || 'Failed to save');
      }
    } catch (error) {
      if (isWeb) window.alert(error.response?.data?.message || 'Failed to save vehicle info');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (docType) => {
    if (!isWeb) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        window.alert('File too large. Maximum 5MB.');
        return;
      }

      setUploading(docType);
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const base64 = reader.result;
            const response = await driverService.uploadDocument(docType, file.name, base64);
            if (response.success) {
              window.alert('Document uploaded! It will be reviewed shortly.');
              loadProfile();
            } else {
              window.alert(response.message || 'Upload failed');
            }
          } catch (err) {
            window.alert(err.response?.data?.message || 'Upload failed');
          } finally {
            setUploading(null);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        window.alert('Failed to read file');
        setUploading(null);
      }
    };
    input.click();
  };

  const getDocByType = (type) => documents.find(d => d.type === type);

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#D4AF37" />
        <Text style={styles.loadingText}>Loading profile...</Text>
      </View>
    );
  }

  const requiredDocs = DOCUMENT_TYPES.filter(d => d.required);
  const allRequiredUploaded = requiredDocs.every(d => {
    const doc = getDocByType(d.type);
    return doc && doc.status !== 'REJECTED';
  });

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      {/* Account Status Banner */}
      <LinearGradient
        colors={driverStatus === 'APPROVED' ? ['#2E7D32', '#1B5E20'] : driverStatus === 'SUSPENDED' ? ['#C62828', '#B71C1C'] : ['#E65100', '#BF360C']}
        style={styles.statusBanner}
      >
        <Ionicons
          name={driverStatus === 'APPROVED' ? 'checkmark-circle' : driverStatus === 'SUSPENDED' ? 'close-circle' : 'time'}
          size={24} color="#fff"
        />
        <View style={styles.statusBannerText}>
          <Text style={styles.statusTitle}>
            {driverStatus === 'APPROVED' ? 'Account Approved' : driverStatus === 'SUSPENDED' ? 'Account Suspended' : 'Pending Approval'}
          </Text>
          <Text style={styles.statusSubtitle}>
            {driverStatus === 'APPROVED'
              ? 'You can go online and accept rides'
              : driverStatus === 'SUSPENDED'
                ? 'Contact support for more information'
                : 'Upload required documents to get approved'}
          </Text>
        </View>
      </LinearGradient>

      {/* Vehicle Information */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="car-sport" size={22} color="#D4AF37" />
          <Text style={styles.sectionTitle}>VEHICLE INFORMATION</Text>
        </View>

        <View style={styles.formRow}>
          <View style={styles.formField}>
            <Text style={styles.fieldLabel}>Make</Text>
            <TextInput style={styles.input} value={vehicleMake} onChangeText={setVehicleMake} placeholder="e.g. Toyota" placeholderTextColor="#555" />
          </View>
          <View style={styles.formField}>
            <Text style={styles.fieldLabel}>Model</Text>
            <TextInput style={styles.input} value={vehicleModel} onChangeText={setVehicleModel} placeholder="e.g. Camry" placeholderTextColor="#555" />
          </View>
        </View>

        <View style={styles.formRow}>
          <View style={styles.formField}>
            <Text style={styles.fieldLabel}>Year</Text>
            <TextInput style={styles.input} value={vehicleYear} onChangeText={setVehicleYear} placeholder="e.g. 2022" placeholderTextColor="#555" keyboardType="numeric" />
          </View>
          <View style={styles.formField}>
            <Text style={styles.fieldLabel}>Color</Text>
            <TextInput style={styles.input} value={vehicleColor} onChangeText={setVehicleColor} placeholder="e.g. Black" placeholderTextColor="#555" />
          </View>
        </View>

        <View style={styles.formRow}>
          <View style={styles.formField}>
            <Text style={styles.fieldLabel}>License Plate</Text>
            <TextInput style={styles.input} value={vehiclePlate} onChangeText={setVehiclePlate} placeholder="e.g. ABC-1234" placeholderTextColor="#555" autoCapitalize="characters" />
          </View>
          <View style={styles.formField}>
            <Text style={styles.fieldLabel}>Phone</Text>
            <TextInput style={styles.input} value={phone} onChangeText={setPhone} placeholder="e.g. +1234567890" placeholderTextColor="#555" keyboardType="phone-pad" />
          </View>
        </View>

        <TouchableOpacity style={styles.saveButton} onPress={handleSaveVehicle} disabled={saving}>
          <LinearGradient colors={saving ? ['#555', '#444'] : ['#D4AF37', '#B8860B']} style={styles.saveGradient}>
            {saving ? (
              <ActivityIndicator color="#000" size="small" />
            ) : (
              <>
                <Ionicons name="save" size={18} color="#000" />
                <Text style={styles.saveText}>SAVE VEHICLE INFO</Text>
              </>
            )}
          </LinearGradient>
        </TouchableOpacity>
      </View>

      {/* Documents Section */}
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Ionicons name="document-text" size={22} color="#D4AF37" />
          <Text style={styles.sectionTitle}>DOCUMENTS</Text>
        </View>

        {!allRequiredUploaded && (
          <View style={styles.docWarning}>
            <Ionicons name="warning" size={16} color="#FF9800" />
            <Text style={styles.docWarningText}>Upload all required documents to go online</Text>
          </View>
        )}

        {DOCUMENT_TYPES.map((docDef) => {
          const doc = getDocByType(docDef.type);
          const isUploading = uploading === docDef.type;

          return (
            <View key={docDef.type} style={styles.docCard}>
              <View style={styles.docCardLeft}>
                <View style={[styles.docIcon, doc && { borderColor: STATUS_COLORS[doc.status] || '#555' }]}>
                  <Ionicons name={docDef.icon} size={20} color={doc ? STATUS_COLORS[doc.status] : '#555'} />
                </View>
                <View style={styles.docInfo}>
                  <View style={styles.docLabelRow}>
                    <Text style={styles.docLabel}>{docDef.label}</Text>
                    {docDef.required && <Text style={styles.docRequired}>REQUIRED</Text>}
                    {!docDef.required && <Text style={styles.docOptional}>OPTIONAL</Text>}
                  </View>
                  {doc ? (
                    <>
                      <View style={[styles.docStatusBadge, { backgroundColor: STATUS_COLORS[doc.status] + '20', borderColor: STATUS_COLORS[doc.status] }]}>
                        <View style={[styles.docStatusDot, { backgroundColor: STATUS_COLORS[doc.status] }]} />
                        <Text style={[styles.docStatusText, { color: STATUS_COLORS[doc.status] }]}>{STATUS_LABELS[doc.status]}</Text>
                      </View>
                      <Text style={styles.docFileName}>{doc.fileName}</Text>
                      {doc.reviewNote && <Text style={styles.docReviewNote}>{doc.reviewNote}</Text>}
                    </>
                  ) : (
                    <Text style={styles.docNotUploaded}>Not uploaded</Text>
                  )}
                </View>
              </View>
              <TouchableOpacity
                style={[styles.docUploadBtn, isUploading && { opacity: 0.5 }]}
                onPress={() => handleUploadDocument(docDef.type)}
                disabled={isUploading}
              >
                {isUploading ? (
                  <ActivityIndicator color="#D4AF37" size="small" />
                ) : (
                  <Ionicons name={doc ? 'refresh' : 'cloud-upload'} size={20} color="#D4AF37" />
                )}
              </TouchableOpacity>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0a0a0a' },
  scrollContent: { paddingBottom: 40 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#0a0a0a' },
  loadingText: { color: '#888', marginTop: 12, fontSize: 14 },

  statusBanner: {
    flexDirection: 'row', alignItems: 'center', padding: 20,
    marginHorizontal: 16, marginTop: 16, borderRadius: 14, gap: 14,
  },
  statusBannerText: { flex: 1 },
  statusTitle: { fontSize: 18, fontWeight: '800', color: '#fff' },
  statusSubtitle: { fontSize: 13, color: 'rgba(255,255,255,0.8)', marginTop: 4, lineHeight: 18 },

  section: {
    marginHorizontal: 16, marginTop: 24,
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 16, padding: 20,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
  },
  sectionHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 20, gap: 10 },
  sectionTitle: { fontSize: 16, fontWeight: '800', color: '#D4AF37', letterSpacing: 2 },

  formRow: { flexDirection: 'row', gap: 12, marginBottom: 14 },
  formField: { flex: 1 },
  fieldLabel: { fontSize: 11, color: 'rgba(255,255,255,0.5)', fontWeight: '700', letterSpacing: 1, marginBottom: 6 },
  input: {
    backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 10,
    paddingHorizontal: 14, paddingVertical: 12, color: '#fff',
    fontSize: 15, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
  },

  saveButton: { marginTop: 6, borderRadius: 12, overflow: 'hidden' },
  saveGradient: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    paddingVertical: 14, gap: 8,
  },
  saveText: { fontSize: 14, fontWeight: '800', color: '#000', letterSpacing: 1 },

  docWarning: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 10,
    padding: 12, marginBottom: 16,
    borderWidth: 1, borderColor: 'rgba(255, 152, 0, 0.3)',
  },
  docWarningText: { fontSize: 13, color: '#FF9800', fontWeight: '600', flex: 1 },

  docCard: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 12,
    padding: 14, marginBottom: 10,
    borderWidth: 1, borderColor: 'rgba(255,255,255,0.06)',
  },
  docCardLeft: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  docIcon: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.06)',
    justifyContent: 'center', alignItems: 'center',
    marginRight: 14, borderWidth: 2, borderColor: '#333',
  },
  docInfo: { flex: 1 },
  docLabelRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  docLabel: { fontSize: 14, fontWeight: '700', color: '#fff' },
  docRequired: { fontSize: 9, fontWeight: '800', color: '#F44336', letterSpacing: 1 },
  docOptional: { fontSize: 9, fontWeight: '800', color: '#666', letterSpacing: 1 },
  docStatusBadge: {
    flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start',
    paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8,
    borderWidth: 1, gap: 5, marginBottom: 2,
  },
  docStatusDot: { width: 6, height: 6, borderRadius: 3 },
  docStatusText: { fontSize: 11, fontWeight: '700' },
  docFileName: { fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 2 },
  docReviewNote: { fontSize: 11, color: '#F44336', marginTop: 2, fontStyle: 'italic' },
  docNotUploaded: { fontSize: 12, color: '#666', fontStyle: 'italic' },
  docUploadBtn: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: 'rgba(212, 175, 55, 0.1)',
    justifyContent: 'center', alignItems: 'center',
    borderWidth: 1, borderColor: 'rgba(212, 175, 55, 0.3)',
    marginLeft: 10,
  },
});

export default DriverAccountScreen;
